import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dumbbell, Apple, Users, Trophy, ArrowLeft, Play, Flame, Target, Calendar, TrendingUp, Heart, Zap, ChevronRight, Star, Check } from 'lucide-react';
import { toast } from 'sonner';

const FitLifeDemo = () => {
  const [activeTab, setActiveTab] = useState('workouts');
  
  const handleStartTrial = () => {
    toast.success('🎉 Kostenlose Testphase gestartet!', {
      description: '7 Tage Premium-Zugang freigeschaltet'
    });
  };

  const workoutCategories = [
    { name: 'Kraft', count: 150, color: 'bg-red-500' },
    { name: 'Cardio', count: 80, color: 'bg-blue-500' },
    { name: 'Yoga', count: 60, color: 'bg-purple-500' },
    { name: 'HIIT', count: 45, color: 'bg-orange-500' },
  ];

  const features = [
    { icon: Dumbbell, title: '500+ Workouts', desc: 'Professionelle Übungen mit HD-Videos' },
    { icon: Apple, title: 'Ernährungspläne', desc: 'Personalisierte Meal-Plans' },
    { icon: Target, title: 'Zieltracking', desc: 'Fortschritte in Echtzeit' },
    { icon: Users, title: 'Community', desc: 'Motivation durch andere' },
    { icon: Calendar, title: 'Trainingspläne', desc: 'Wöchentliche Programme' },
    { icon: TrendingUp, title: 'Analytics', desc: 'Detaillierte Statistiken' },
  ];

  const testimonials = [
    { name: 'Lisa M.', result: '-15kg in 3 Monaten', text: 'Diese App hat mein Leben verändert!', img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80' },
    { name: 'Thomas K.', result: '+8kg Muskelmasse', text: 'Endlich strukturiertes Training!', img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80' },
  ];

  const pricingPlans = [
    { name: 'Free', price: '0', features: ['10 Workouts/Monat', 'Basis-Tracking', 'Community Zugang'] },
    { name: 'Pro', price: '9.99', features: ['Unbegrenzte Workouts', 'Ernährungspläne', 'Persönlicher Coach', 'Offline-Modus', 'Keine Werbung'], popular: true },
    { name: 'Team', price: '19.99', features: ['Alles aus Pro', 'Team-Challenges', 'Firmen-Dashboard', 'Prioritäts-Support'] },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm">
            <ArrowLeft size={16} className="mr-2" />
            Zurück zum Portfolio
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-slate-900/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <Dumbbell className="text-white" size={20} />
            </div>
            <span className="text-white font-bold text-lg">FitLife Coach</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <a href="#features" className="hover:text-emerald-400">Features</a>
            <a href="#pricing" className="hover:text-emerald-400">Preise</a>
            <a href="#download" className="hover:text-emerald-400">Download</a>
          </div>
          <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
            Kostenlos starten
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-emerald-900/20 to-slate-900 pt-16">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&q=80')] bg-cover bg-center opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 mb-6">
                <Flame className="text-emerald-400" size={16} />
                <span className="text-emerald-300 text-sm font-medium">#1 Fitness App 2024</span>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight">
                Dein Körper.
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400"> Deine Regeln.</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Die smarteste Fitness-App mit KI-gesteuerten Trainingsplänen, Ernährungscoach und einer motivierenden Community.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" onClick={handleStartTrial} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8">
                  <Play size={18} className="mr-2" />
                  Kostenlos starten
                </Button>
                <Button size="lg" onClick={() => toast.info('📱 App-Download', { description: 'Verfügbar im App Store und Google Play' })} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  App herunterladen
                </Button>
              </div>
              
              {/* Stats */}
              <div className="mt-12 grid grid-cols-3 gap-8">
                {[
                  { value: '500K+', label: 'Aktive Nutzer' },
                  { value: '4.9', label: 'App Store' },
                  { value: '10M+', label: 'Workouts' },
                ].map((stat, i) => (
                  <div key={i}>
                    <div className="text-3xl font-bold text-emerald-400">{stat.value}</div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* App Preview */}
            <div className="hidden lg:block relative">
              <div className="relative mx-auto w-72 h-[580px] bg-slate-800 rounded-[3rem] p-3 shadow-2xl border border-slate-700">
                <div className="w-full h-full bg-slate-900 rounded-[2.5rem] overflow-hidden">
                  <div className="p-6">
                    <div className="text-white font-bold text-lg mb-4">Heute</div>
                    <div className="bg-emerald-500/20 rounded-xl p-4 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-emerald-400 font-medium">Kraft Training</span>
                        <span className="text-slate-400 text-sm">45 min</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div className="bg-emerald-500 h-2 rounded-full" style={{width: '65%'}}></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {workoutCategories.map((cat, i) => (
                        <div key={i} className="bg-slate-800 rounded-xl p-3">
                          <div className={`w-8 h-8 ${cat.color} rounded-lg mb-2`}></div>
                          <div className="text-white text-sm font-medium">{cat.name}</div>
                          <div className="text-slate-500 text-xs">{cat.count} Übungen</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Features</span>
            <h2 className="text-4xl font-bold text-slate-900 mt-3">Alles was du brauchst</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <Card key={i} className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                    <feature.icon className="text-emerald-600 group-hover:text-white transition-colors" size={28} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Preise</span>
            <h2 className="text-4xl font-bold text-slate-900 mt-3">Wähle deinen Plan</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <Card key={i} className={`border-2 ${plan.popular ? 'border-emerald-500 shadow-xl scale-105' : 'border-slate-200'} relative`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Beliebt
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">{plan.price}€</span>
                    <span className="text-slate-500">/Monat</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3">
                        <Check className="text-emerald-500" size={18} />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className={`w-full ${plan.popular ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-slate-900 hover:bg-slate-800'} text-white`}>
                    Jetzt starten
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white">Erfolgsgeschichten</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((t, i) => (
              <Card key={i} className="bg-white/10 backdrop-blur-sm border-0">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <img src={t.img} alt={t.name} className="w-14 h-14 rounded-full object-cover" />
                    <div>
                      <div className="text-white font-semibold">{t.name}</div>
                      <div className="text-emerald-200 text-sm">{t.result}</div>
                    </div>
                  </div>
                  <p className="text-white/90">"{t.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="download" className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Starte noch heute</h2>
          <p className="text-slate-400 mb-8 text-lg">Lade die App kostenlos herunter und beginne deine Transformation.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
              <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              App Store
            </Button>
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
              <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>
              Google Play
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-500 text-sm">
          © 2024 FitLife Coach. Alle Rechte vorbehalten.
        </div>
      </footer>

      {/* Demo Badge */}
      <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg z-50">
        Demo-Projekt von Beta-Webagentur
      </div>
    </div>
  );
};

export default FitLifeDemo;
